import * as React from 'react';
import { RadioGroup as RadioGroupPrimitive } from 'radix-ui';

import { cn } from '@/lib/core';

// ── RadioGroup ────────────────────────────────────────────────────────────────

interface RadioGroupProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
}

function RadioGroup({ className, ...props }: RadioGroupProps) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn('flex flex-col gap-2', className)}
      {...(props as any)}
    />
  );
}

// ── RadioGroupItem ────────────────────────────────────────────────────────────

interface RadioGroupItemProps {
  id?: string;
  value: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  className?: string;
  [key: string]: unknown;
}

function RadioGroupItem({ className, error, ...props }: RadioGroupItemProps) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      aria-invalid={error ? 'true' : undefined}
      className={cn(
        'group relative size-5 shrink-0 rounded-full border-[0.5px] transition-colors',
        'focus-visible:ring-2 focus-visible:ring-[#848484]/40 focus-visible:ring-offset-1 focus-visible:outline-none',
        // Default (unchecked)
        'border-[#848484] bg-white',
        // Pressed
        'active:bg-[#f4f4f4]',
        // Selected
        'data-[state=checked]:border-transparent data-[state=checked]:bg-[#f4f4f4]',
        // Error
        'aria-invalid:border-[#971b2f]',
        // Disabled
        'data-disabled:cursor-not-allowed data-disabled:border-transparent data-disabled:bg-[#eaeaea]',
        className
      )}
      {...(props as any)}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <span className="bg-jncquoi-neutral-700 size-2 rounded-full group-data-disabled:bg-[#a9a9a9]" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

// ── RadioGroupLabel ───────────────────────────────────────────────────────────
// Primary label for a radio field — 12px PP Neue Montreal Book, neutral-800.

interface RadioGroupLabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  className?: string;
}

function RadioGroupLabel({ className, ...props }: RadioGroupLabelProps) {
  return (
    <label
      data-slot="radio-group-label"
      className={cn(
        'font-pp-neue-montreal cursor-pointer text-[12px] leading-none font-normal tracking-[0.5px] text-[#666666]',
        className
      )}
      {...props}
    />
  );
}

// ── RadioGroupSupportText ─────────────────────────────────────────────────────
// Secondary hint/description below the label — 12px PP Neue Montreal Book, neutral-700.

interface RadioGroupSupportTextProps extends React.HTMLAttributes<HTMLSpanElement> {
  className?: string;
}

function RadioGroupSupportText({ className, ...props }: RadioGroupSupportTextProps) {
  return (
    <span
      data-slot="radio-group-support-text"
      className={cn(
        'font-pp-neue-montreal text-[12px] leading-none font-normal tracking-[0.5px] text-[#848484]',
        className
      )}
      {...props}
    />
  );
}

// ── RadioGroupField ───────────────────────────────────────────────────────────
// Convenience component: RadioGroupItem + label column (label + optional support text).
// Clicking anywhere in the field selects the item via htmlFor association.

interface RadioGroupFieldProps {
  id: string;
  value: string;
  label: string;
  supportText?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
}

function RadioGroupField({
  id,
  value,
  label,
  supportText,
  disabled,
  error,
  className,
}: RadioGroupFieldProps) {
  return (
    <div
      data-slot="radio-group-field"
      className={cn('flex items-start gap-2', disabled && 'opacity-40', className)}
    >
      <RadioGroupItem id={id} value={value} disabled={disabled} error={error} />
      <div className="flex min-h-5 flex-col items-center justify-center gap-1">
        <RadioGroupLabel htmlFor={id}>{label}</RadioGroupLabel>
        {supportText && <RadioGroupSupportText>{supportText}</RadioGroupSupportText>}
      </div>
    </div>
  );
}

export { RadioGroup, RadioGroupItem, RadioGroupLabel, RadioGroupSupportText, RadioGroupField };
